import type React from 'react';
import {
  createContext,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

interface TitleContextProps {
  prefix?: string;
  suffix?: string;
  setPrefix: (prefix: string) => void;
  setSuffix: (suffix: string) => void;
}

interface TitleProviderProps {
  children: ReactNode;
  defaultPrefix?: string;
  defaultSuffix?: string;
}

const TitleContext = createContext<TitleContextProps | undefined>(undefined);

export const TitleProvider: React.FC<TitleProviderProps> = ({
  children,
  defaultPrefix,
  defaultSuffix,
}) => {
  const [prefix, setPrefix] = useState<string | undefined>(defaultPrefix);
  const [suffix, setSuffix] = useState<string | undefined>(defaultSuffix);

  const value = useMemo(
    () => ({ prefix, suffix, setPrefix, setSuffix }),
    [prefix, suffix],
  );

  return (
    <TitleContext.Provider value={value}>{children}</TitleContext.Provider>
  );
};

export function useTitleContext() {
  const context = useContext(TitleContext);
  if (!context) {
    throw new Error('useTitle must be used within a TitleProvider');
  }

  return context;
}
